
import React, { PureComponent } from 'react';

import { connect } from '../../../features/base/redux';
import { translate } from '../../base/i18n/functions';
import { safePlayMediaElement } from '../../base/media/playbackUtils';
import { getNoiseSuppressionEnabledOnStartup as isNoiseSuppressionEnabled, setNoiseSuppressionEnabledSetting } from '../../noise-suppression/functions';
import logger from '../logger';

import AudioOutputPreviewContent from './AudioOutputPreviewContent';

/**
 * The type of the React {@code Component} props of {@link AudioOutputPreview}.
 */
type Props = {

    /**
     * The device id of the audio output device to use.
     */
    deviceId: string,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    /**
     * Whether noise suppression is enabled.
     */
    _isNoiseSuppressionEnabled: boolean,

    /**
     * Redux dispatch function.
     */
    dispatch: Function
};

/**
 * React component for playing a test sound through a specified audio device.
 *
 * @augments Component
 */
class AudioOutputPreview extends PureComponent<Props> {
    _audioElement: ?Object;

    /**
     * Initializes a new AudioOutputPreview instance.
     *
     * @param {Object} props - The read-only React Component props with which
     * the new instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this._audioElement = null;

        this._audioElementReady = this._audioElementReady.bind(this);
        this._onClick = this._onClick.bind(this);
        this._playTestSound = this._playTestSound.bind(this);
        this._onNoiseSuppressionChange = this._onNoiseSuppressionChange.bind(this);
    }

    /**
     * Updates the audio element when the target output device changes and the
     * audio element has re-rendered.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidUpdate() {
        this._setAudioSink();
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        return (
            <AudioOutputPreviewContent
                audioElementReady = { this._audioElementReady }
                interfaceConfig = { this.props.interfaceConfig }
                isNoiseSuppressionEnabledProps = { this.props._isNoiseSuppressionEnabled }
                onClick = { this._onClick }
                onNoiseSuppressionChange = { this._onNoiseSuppressionChange }
                t = { this.props.t } />
        );
    }

    _audioElementReady: (Object) => void;

    /**
     * Sets the instance variable for the component's audio element so it can be
     * accessed directly.
     *
     * @param {Object} element - The DOM element for the component's audio.
     * @private
     * @returns {void}
     */
    _audioElementReady(element: Object) {
        this._audioElement = element;

        this._setAudioSink();
    }

    _onClick: () => void;

    /**
     * Plays a test sound.
     *
     * @private
     * @returns {void}
     */
    _onClick() {
        const element = this._audioElement;

        if (!element || !element.isConnected) {
            return;
        }

        this._setAudioSink();
        this._playTestSound(element, true);
    }

    _playTestSound: (Object, boolean) => void;

    /**
     * Plays the test sound, reloading its source once if the element is stuck in an error state.
     *
     * @param {HTMLAudioElement} element - The test sound element.
     * @param {boolean} allowReload - Whether a NotSupportedError may trigger a reload and a second attempt.
     * @private
     * @returns {void}
     */
    _playTestSound(element: Object, allowReload: boolean) {
        safePlayMediaElement(element, {
            onAutoplayBlocked: error => {
                logger.warn('Audio output preview play blocked by browser policy:', error);
            },
            onError: error => {
                if (allowReload && error?.name === 'NotSupportedError') {
                    const handlers = {};

                    handlers.canplay = () => {
                        element.removeEventListener('error', handlers.error);
                        this._playTestSound(element, false);
                    };
                    handlers.error = () => {
                        element.removeEventListener('canplay', handlers.canplay);
                        logger.error('Audio output preview play failed:', error);
                    };

                    element.addEventListener('canplay', handlers.canplay, { once: true });
                    element.addEventListener('error', handlers.error, { once: true });
                    element.load();

                    return;
                }

                logger.error('Audio output preview play failed:', error);
            }
        });
    }

    _onNoiseSuppressionChange: (Object) => void;

    /**
     * Handles the noise suppression checkbox change.
     *
     * @param {Object} e - The change event.
     * @private
     * @returns {void}
     */
    _onNoiseSuppressionChange(e: Object) {
        const { dispatch } = this.props;

        dispatch(setNoiseSuppressionEnabledSetting(e));
    }

    /**
     * Updates the target output device for playing the test sound.
     *
     * @private
     * @returns {void}
     */
    _setAudioSink() {
        if (!this._audioElement || !this.props.deviceId || typeof this._audioElement.setSinkId !== 'function') {
            return;
        }

        // setSinkId returns a Promise on HTMLMediaElement, but may return undefined
        // depending on the underlying implementation or platform.
        const sinkPromise = this._audioElement.setSinkId(this.props.deviceId);

        if (sinkPromise && typeof sinkPromise.catch === 'function') {
            sinkPromise.catch(err => {
                logger.warn('Audio sink switch failed', err);
            });
        }
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code AudioOutputPreview}'s
 * props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     interfaceConfig: Object,
 *     _isNoiseSuppressionEnabled: boolean
 * }}
 */
function _mapStateToProps(state) {
    const { interfaceConfig } = state['features/base/config'];

    return {
        interfaceConfig,
        _isNoiseSuppressionEnabled: isNoiseSuppressionEnabled(state)
    };
}

/**
 * Connects the AudioOutputPreview component to Redux and translation HOC.
 */
export default connect(_mapStateToProps)(translate(AudioOutputPreview));
